import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import dayjs from "dayjs"
import {
    Button, Card, CardBody, Col, Container, Form, FormGroup,
    Input, Label, Row,
} from "reactstrap"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import PlantSelect from "./PlantSelect"

import { getAreas, updateArea } from "../../store/goals/actions"
import { getProfile } from "../../store/auth/profile/actions"

const PlantsLogHarvest = (props) => {
    const { userareas, onGetAreas, onGetProfile, onUpdateArea } = props

    const username = JSON.parse(localStorage.getItem("user") || "{}").user || ""
    const [unitmeasurement] = useState("imperial")

    const [includeVarieties, setIncludeVarieties] = useState(true)
    const [logHarvestDate, setLogHarvestDate] = useState(new Date())
    const [logHarvestRows, setLogHarvestRows] = useState([{ plantName: "", variety: "", ounces: 0 }])
    const [harvestPlantOptions, setHarvestPlantOptions] = useState([])
    const [successMsg, setSuccessMsg] = useState("")

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("user"))
        if (user) onGetProfile(user.user)
        onGetAreas()
    }, [])

    useEffect(() => {
        const unique = new Set()
        userareas.forEach(area => {
            area.plants.forEach(plant => {
                const name = plant.name || plant.species
                const variety = plant.variety || ""
                const key = includeVarieties ? `${name}__${variety}` : `${name}__`
                unique.add(key)
            })
        })
        const options = Array.from(unique)
            .map(key => {
                const [name, variety] = key.split("__")
                return {
                    key,
                    label: includeVarieties && variety ? `${name} (${variety})` : name
                }
            })
            .sort((a, b) => a.label.localeCompare(b.label))
        setHarvestPlantOptions(options)
    }, [userareas, includeVarieties])

    const formatDateToISO = (date) => dayjs(date).format("YYYY-MM-DD")

    const handleRowChange = (index, field, value) => {
        const updated = [...logHarvestRows]
        if (field === "plantKey") {
            const [plantName, variety] = value.split("__")
            updated[index].plantName = plantName
            updated[index].variety = variety || ""
        } else {
            updated[index][field] = value
        }
        setLogHarvestRows(updated)
    }

    const addRow = () => setLogHarvestRows([...logHarvestRows, { plantName: "", variety: "", ounces: 0 }])

    const removeRow = (index) => {
        const updated = [...logHarvestRows]
        updated.splice(index, 1)
        setLogHarvestRows(updated)
    }

    const saveHarvests = async () => {
        const unit = unitmeasurement === "imperial" ? "ounces" : "grams"
        const clonedAreas = userareas.map(area => ({
            ...area,
            plants: area.plants.map(p => ({ ...p }))
        }))
        const updatedAreasMap = new Map()

        logHarvestRows.forEach(row => {
            const { plantName, variety, ounces } = row
            const matched = []
            let totalPlants = 0

            clonedAreas.forEach(area => {
                area.plants.forEach((plant, plantIndex) => {
                    const nameMatch = (plant.name === plantName) || (plant.species === plantName)
                    const varietyMatch = includeVarieties ? (plant.variety || "") === (variety || "") : true
                    if (nameMatch && varietyMatch) {
                        const rawNum = plant.numPlants
                        const groupSize = typeof rawNum === "object" && rawNum?.N != null
                            ? parseInt(rawNum.N, 10)
                            : parseInt(rawNum || 0, 10)
                        totalPlants += groupSize
                        matched.push({ areaId: area.id, area, plantIndex, groupSize })
                    }
                })
            })

            if (matched.length === 0 || totalPlants === 0) return

            matched.forEach(({ areaId, area, plantIndex, groupSize }) => {
                const proportion = groupSize / totalPlants
                const amount = parseFloat((proportion * parseFloat(ounces)).toFixed(1))
                const harvestItem = { date: formatDateToISO(logHarvestDate), amount, unit }
                const plant = area.plants[plantIndex]
                const existing = Array.isArray(plant.harvests) ? plant.harvests : (plant.harvests?.L || [])
                plant.harvests = [...existing, harvestItem]
                plant.totalHarvest = plant.harvests.reduce((sum, h) => sum + parseFloat(h.amount || 0), 0)
                if (updatedAreasMap.has(areaId)) {
                    updatedAreasMap.get(areaId).plants[plantIndex] = plant
                } else {
                    updatedAreasMap.set(areaId, area)
                }
            })
        })

        try {
            await Promise.all(Array.from(updatedAreasMap.values()).map(area => onUpdateArea(area)))
            setLogHarvestRows([{ plantName: "", variety: "", ounces: 0 }])
            setSuccessMsg("Harvests saved!")
            setTimeout(() => setSuccessMsg(""), 3000)
        } catch (err) {
            console.error("Error saving harvest logs:", err)
        }
    }

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col md={8} lg={6}>
                        <Card>
                            <CardBody>
                                <h4 className="mb-4">Log Harvest</h4>
                                <Form>
                                    <FormGroup check className="mb-3">
                                        <Input
                                            type="checkbox"
                                            id="include-varieties"
                                            checked={includeVarieties}
                                            onChange={() => setIncludeVarieties(prev => !prev)}
                                        />
                                        <Label check for="include-varieties">Include Varieties</Label>
                                    </FormGroup>

                                    <Row className="mb-3">
                                        <Col md={4}><Label>Harvest Date</Label></Col>
                                        <Col md={8}>
                                            <DatePicker
                                                selected={logHarvestDate}
                                                onChange={(date) => setLogHarvestDate(date)}
                                                dateFormat="yyyy-MM-dd"
                                                className="form-control"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="fw-bold small text-muted mb-1">
                                        <Col md={7}>Plant / Variety</Col>
                                        <Col md={3}>{unitmeasurement === "imperial" ? "Ounces" : "Grams"}</Col>
                                        <Col md={2}></Col>
                                    </Row>

                                    {logHarvestRows.map((row, index) => (
                                        <Row key={index} className="mb-2 align-items-center">
                                            <Col md={7}>
                                                <PlantSelect
                                                    plantOptions={harvestPlantOptions}
                                                    value={row.plantName + "__" + row.variety}
                                                    onChange={(val) => handleRowChange(index, "plantKey", val)}
                                                />
                                            </Col>
                                            <Col md={3}>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.1"
                                                    value={row.ounces}
                                                    onChange={e => handleRowChange(index, "ounces", e.target.value)}
                                                />
                                            </Col>
                                            <Col md={2} className="text-end">
                                                {logHarvestRows.length > 1 && (
                                                    <i
                                                        className="fas fa-trash"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => removeRow(index)}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    ))}

                                    <div className="mt-2 mb-4">
                                        <span
                                            style={{ cursor: "pointer", color: "#007bff", textDecoration: "underline", fontSize: "0.9rem" }}
                                            onClick={addRow}
                                        >
                                            + Add another harvest
                                        </span>
                                    </div>

                                    {successMsg && <div className="text-success mb-3">{successMsg}</div>}

                                    <Row style={{ paddingTop: "10px" }}>
                                        <Col md={4} />
                                        <Col md={4}>
                                            <Button onClick={saveHarvests} color="success" className="btn-sm">
                                                Save Harvests
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

const mapStateToProps = (state) => ({
    userareas: (state.goals.areas || [])
        .map(area => {
            const copy = structuredClone(area)
            copy.plants = copy.plants.filter(p => p.species !== undefined)
            return copy
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
})

const mapDispatchToProps = (dispatch) => ({
    onGetProfile: (username) => dispatch(getProfile(username)),
    onGetAreas: () => dispatch(getAreas()),
    onUpdateArea: (area) => dispatch(updateArea(area)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PlantsLogHarvest)
